import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useProducts } from '@/contexts/ProductsContext';

export default function RoutineSection() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { products } = useProducts();

  if (products.length === 0) return null;

  // Progressive beige palette: white -> light beige -> warm beige
  const cardStyles = [
    {
      bg: 'bg-white',
      textMain: 'text-[#2A2A2A]',
      textMuted: 'text-[#8A8A8A]',
      priceColor: 'text-[#2A2A2A]',
      claimBg: 'bg-[#F0EFED] text-[#8A8A8A]',
      flowBg: 'bg-white',
      flowText: 'text-[#2A2A2A]',
    },
    {
      bg: 'bg-[#EDE5DB]',
      textMain: 'text-[#2A2A2A]',
      textMuted: 'text-[#8A7A70]',
      priceColor: 'text-[#2A2A2A]',
      claimBg: 'bg-[#E5DCD4] text-[#7A7068]',
      flowBg: 'bg-[#EDE5DB]',
      flowText: 'text-[#2A2A2A]',
    },
    {
      bg: 'bg-[#C8A882]',
      textMain: 'text-[#2A2A2A]',
      textMuted: 'text-[#5A4A3A]',
      priceColor: 'text-[#2A2A2A]',
      claimBg: 'bg-[#B89870] text-[#3A2E22]',
      flowBg: 'bg-[#C8A882]',
      flowText: 'text-white',
    },
  ];

  return (
    <section id="routine" className="w-full bg-[#F0EFED] py-20 md:py-28 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-16">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-[#7B1113]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
                {t('home.routine.label')}
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#2A2A2A] leading-[1.15]">
              {t('home.routine.heading')}
            </h2>
          </div>
          <span className="text-sm font-light text-[#8A8A8A] tracking-wide">
            {t('home.routine.tagline')}
          </span>
        </div>

        {/* 3-Step Flow visual */}
        <div className="hidden md:flex items-center justify-center gap-2 mb-10">
          {products.map((product, index) => (
            <div key={product.id} className="flex items-center gap-2">
              <div
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full ${cardStyles[index].flowBg} ${cardStyles[index].flowText}`}
              >
                <span className="text-[10px] font-bold">0{product.step}</span>
                <span className="text-[10px] tracking-[0.1em] uppercase font-medium">
                  {product.stepKeyword}
                </span>
              </div>
              {index < 2 && (
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#D9CFC5]/60">
                  <i className="ri-arrow-right-line text-[#7A6E64] text-[10px]" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Product cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {products.map((product, index) => {
            const style = cardStyles[index];

            return (
              <div key={product.id} className="relative h-full">
                {/* Flow arrow between cards (desktop) */}
                {index < 2 && (
                  <div className="hidden md:flex absolute top-1/2 -right-5 z-20 -translate-y-1/2 w-8 h-8 rounded-full bg-[#7B1113] items-center justify-center shadow-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-white text-xs" />
                    </div>
                  </div>
                )}

                <div
                  className={`relative flex flex-col rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-lg h-full ${style.bg}`}
                  onMouseEnter={() => setHoveredId(product.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {/* Step badge */}
                  <div className="absolute top-5 left-5 z-10">
                    <div className="flex items-center gap-2">
                      <span className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold bg-[#7B1113] text-white">
                        0{product.step}
                      </span>
                      <span className={`text-[10px] tracking-[0.15em] uppercase font-medium ${style.textMuted}`}>
                        {product.stepKeyword}
                      </span>
                    </div>
                  </div>

                  {/* Product image */}
                  <Link
                    to={`${prefix}/products/${product.id}`}
                    className="relative w-full aspect-square overflow-hidden flex items-center justify-center cursor-pointer flex-shrink-0"
                  >
                    <img
                      src={product.image}
                      alt={product.imageAlt}
                      className={`w-full h-full object-cover transition-transform duration-500 ${
                        hoveredId === product.id ? 'scale-110' : 'scale-100'
                      }`}
                    />
                  </Link>

                  {/* Product info */}
                  <div className="flex flex-col gap-3 p-6 pt-2 flex-1">
                    <Link
                      to={`${prefix}/products/${product.id}`}
                      className="flex flex-col gap-1 cursor-pointer"
                    >
                      <h3 className={`font-serif text-lg font-semibold leading-tight hover:text-[#7B1113] transition-colors ${style.textMain}`}>
                        {product.name}
                      </h3>
                      <span className={`text-xs font-light ${style.textMuted}`}>
                        {product.subtitle}
                      </span>
                    </Link>

                    <span className={`text-sm font-medium ${style.priceColor}`}>
                      {product.price}
                    </span>

                    <p className={`text-xs leading-[1.7] font-light flex-1 ${style.textMuted}`}>
                      {product.description}
                    </p>

                    {/* Ingredient tags */}
                    <div className="flex flex-wrap gap-1.5">
                      {product.claims.slice(0, 3).map((claim) => (
                        <span
                          key={claim}
                          className={`text-[9px] tracking-[0.08em] uppercase px-2.5 py-1 rounded-full font-medium ${style.claimBg}`}
                        >
                          {claim}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="flex justify-center mt-12">
          <Link
            to={prefix + '/3-step-treatment'}
            className="w-full sm:w-auto px-8 py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
          >
            {t('home.routine.cta')}
          </Link>
        </div>
      </div>
    </section>
  );
}